import {
    Box,
    Typography,
    Card,
    Grid,
    Button,
    Chip,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material'
import {
    ContentCopy as CopyIcon,
    Visibility as ViewIcon,
    Phone as PhoneIcon
} from '@mui/icons-material'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { formatPhoneNumber } from '../../utils/formatPhoneNumber'

interface ProspectData {
    id: string;
    name: string;
    phone_number: string | null;
    created_at: string;
    search_status: string;
    move_in_date_requested: string | null;
    budget: number | null;
    tour_requests_count: number;
    upcoming_tours_count: number;
    next_tour: {
        date: string;
        time: string;
        address: string;
    } | null;
}

interface AdminRenterCardProps {
    prospect: ProspectData;
    isFullCustomer: boolean;
}

export default function AdminRenterCard({ prospect, isFullCustomer }: AdminRenterCardProps) {
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const handleCopyLink = async () => {
        const url = `${window.location.origin}/view-profile/${prospect.id}`;
        try {
            await navigator.clipboard.writeText(url);
            setSnackbarOpen(true);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    const handleView = () => {
        router.visit(`/admin/renters/${prospect.id}`);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatSearchStatus = (status: string) => {
        return status.replace('_', ' ').split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    return (
        <>
            <Card
                sx={{
                    p: 3,
                    mb: 2,
                    borderRadius: '8px',
                    boxShadow: isFullCustomer
                        ? '0px 2px 8px rgba(0,0,0,0.1)'
                        : '0px 1px 4px rgba(0,0,0,0.08)',
                    border: isFullCustomer ? 'none' : '1px solid #e0e0e0'
                }}
            >
                <Grid container spacing={2}>
                    {/* Prospect Name and Status */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            {prospect.name}
                        </Typography>
                        <Chip
                            label={formatSearchStatus(prospect.search_status)}
                            size="small"
                            color="primary"
                            sx={{ mb: 1 }}
                        />
                        {isFullCustomer && (
                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                                Full Customer
                            </Typography>
                        )}
                    </Grid>

                    {/* Tour Stats */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Tour Requests:</strong> {prospect.tour_requests_count}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Upcoming Tours:</strong> {prospect.upcoming_tours_count}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Key Info */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Created:</strong> {formatDate(prospect.created_at)}
                        </Typography>
                        {prospect.phone_number && (
                            <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 16, color: 'grey.600' }} />
                                <strong>Phone:</strong> {formatPhoneNumber(prospect.phone_number)}
                            </Typography>
                        )}
                        {prospect.move_in_date_requested && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Move-in Date:</strong> {prospect.move_in_date_requested}
                            </Typography>
                        )}
                        {isFullCustomer && prospect.budget && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Budget:</strong> ${prospect.budget.toLocaleString()}
                            </Typography>
                        )}
                    </Grid>

                    {/* Next Tour */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Next Tour:</strong>{' '}
                            {prospect.next_tour ? (
                                <>
                                    {prospect.next_tour.date} at {prospect.next_tour.time}<br />
                                    <Typography variant="body2" color="text.secondary" component="span">
                                        {prospect.next_tour.address}
                                    </Typography>
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary" component="span">
                                    No upcoming tour
                                </Typography>
                            )}
                        </Typography>
                    </Grid>

                    {/* Schedule Tour Link */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                <strong>Schedule Tour Link:</strong>{' '}
                                <a
                                    href={`${window.location.origin}/renters/view-profile/${prospect.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#1976d2', textDecoration: 'none' }}
                                >
                                    {`${window.location.origin}/renters/view-profile/${prospect.id}`}
                                </a>
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={handleCopyLink}
                                sx={{ color: 'primary.main' }}
                            >
                                <CopyIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Grid>

                    {/* View Button */}
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            startIcon={<ViewIcon />}
                            onClick={handleView}
                            fullWidth
                            sx={{
                                bgcolor: 'black',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'grey.800'
                                }
                            }}
                        >
                            View Details
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
                    Link copied to clipboard!
                </Alert>
            </Snackbar>
        </>
    );
}

